import React, { useEffect, useMemo, useState } from "react";
import { Grid } from "@mui/material";
import CustomTextField from "./CustomTextField";
import { Control, Controller, UseFormSetValue } from "react-hook-form";
import CustomDropdownField from "./CustomDropdownFiled";
import {
  CodeTable,
  getCodeTableValuesByTableName,
  useCodetablesSlice,
} from "../../redux/slice/codetableSlice";
import CustomAutocompleteField from "./CustomAutocompleteField";
import {
  autoAddress,
  fetchAddressDetails,
} from "../../redux/slice/commonSlice";
import { useAppDispatch } from "../../redux/slice/redux-hooks";

interface AddressTextFieldData {
  streetaddress: string;
  suite: string;
  city: string;
  state: string;
  zipCode: string;
}

export type AddressTextFieldProps = {
  addressFieldsInfo?: any;
  onAddressFieldsChange?: (data: AddressTextFieldData, name: any) => void;
  context?: string;
  name?: string;
  control?: Control<any, any>;
  xs?: number;
  md?: number;
  lg?: number;
  isRequired?: boolean;
  isDisabled?: boolean;
  useAutoComplete?: boolean;
  onChange?: (event: React.ChangeEvent<HTMLInputElement>) => void;
  setValue?: UseFormSetValue<any>;
  inputFieldSx?: object;
  size?: "small" | "medium";
  usePlaceholder?: boolean;
};

const CustomAddressTextField: React.FC<AddressTextFieldProps> = ({
  control,
  addressFieldsInfo,
  onAddressFieldsChange,
  context = "",
  name = "",
  xs,
  md,
  lg,
  isRequired,
  isDisabled,
  useAutoComplete,
  onChange,
  setValue,
  inputFieldSx,
  size = "medium",
  usePlaceholder = false,
  ...props
}) => {
  const streetAddressLabel = usePlaceholder ? "" : `${context} Street Address`;
  const unitNumberLabel = usePlaceholder ? "" : `${context} Unit Number`;
  const cityLabel = usePlaceholder ? "" : `${context} City`;
  const stateLabel = usePlaceholder ? "" : `${context} State`;
  const zipLabel = usePlaceholder ? "" : `${context} Zip Code`;
  const dispatch = useAppDispatch();

  const handleFieldChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (onChange) {
      onChange(event);
    }
  };

  const { allCodeTablesValuesMap, allCodeTableValueFetchStatus } =
    useCodetablesSlice();
  const [stateList, setStateList] = React.useState<CodeTable[]>([]);

  const stateListOptions = useMemo(() => {
    return stateList.map((item) => ({
      label: item.displayValue,
      value: item.keyName,
    }));
  }, [stateList]);

  useEffect(() => {
    if (allCodeTableValueFetchStatus === "success" && allCodeTablesValuesMap) {
      const sortedStateList = [
        ...getCodeTableValuesByTableName(allCodeTablesValuesMap, "StateList"),
      ].sort((a, b) => a.displayValue.localeCompare(b.displayValue));
      setStateList(sortedStateList);
    }
  }, [allCodeTablesValuesMap, allCodeTableValueFetchStatus]);

  const fetchStreetSuggestions = async (query: string) => {
    if (query.length < 2) return [];
    try {
      const response = await dispatch(
        autoAddress({
          addressInput: query,
          interceptorOption: { skipErrorPopup: true },
        }),
      ).unwrap();
      return response;
    } catch (error) {
      console.error("Error fetching address suggestions:", error);
      return [];
    }
  };

  const transformAddressResponse = (response: any): any[] => {
    const returnRes = (response?.data || []).map((item: any) => ({
      value: item.description,
      data: item.placeId,
      label: item.description,
    }));
    return returnRes;
  };

  const fetchDetails = useMemo(
    () => async (placeId: string) => {
      try {
        const response = await dispatch(
          fetchAddressDetails({
            placeID: placeId,
            interceptorOption: { skipErrorPopup: true },
          }),
        ).unwrap();
        if (response?.data) {
          const { streetAddress, unitNumber, city, state, zipCode } =
            response.data;

          const data: AddressTextFieldData = {
            streetaddress: streetAddress,
            suite: unitNumber || "",
            city,
            state,
            zipCode,
          };

          const prefix = name ? `${name}.` : "";

          if (setValue) {
            setValue(
              name
                ? `${prefix}${context.toLowerCase()}StreetAddress`
                : `${context.toLowerCase()}streetAddress`,
              streetAddress,
            );
            setValue(
              name
                ? `${prefix}${context.toLowerCase()}UnitNumber`
                : `${context.toLowerCase()}unitNumber`,
              unitNumber || "",
            );
            setValue(
              name
                ? `${prefix}${context.toLowerCase()}City`
                : `${context.toLowerCase()}city`,
              city,
            );
            setValue(
              name
                ? `${prefix}${context.toLowerCase()}State`
                : `${context.toLowerCase()}state`,
              state,
            );
            setValue(
              name
                ? `${prefix}${context.toLowerCase()}Zipcode`
                : `${context.toLowerCase()}zipCode`,
              zipCode,
            );
          }
        }
      } catch (error) {
        console.error("Error fetching address details:", error);
      }
    },
    [dispatch, name, context],
  );

  // ── name branch (nested field path) ──────────────────────────────────────
  if (name)
    return (
      <Controller
        name={name}
        control={control}
        render={({ field, fieldState }) => (
          <Grid container spacing={2}>
            <Grid item xs={xs ?? 12} md={md ?? 4} lg={lg ?? 4}>
              {!useAutoComplete ? (
                <CustomTextField
                  controllerName={`${field.name}.${context.toLowerCase()}StreetAddress`}
                  control={control}
                  label={streetAddressLabel}
                  placeholder={usePlaceholder ? "Street Address" : undefined}
                  isDisabled={isDisabled}
                  onChange={handleFieldChange}
                  size={size}
                  rules={
                    isRequired
                      ? {
                          required: `${context} Street Address is required.`,
                          validate: {
                            customValidation: (value: string) => {
                              if (value?.trim() === "") {
                                return `${context} Street Address is required.`;
                              }
                            },
                          },
                        }
                      : {}
                  }
                />
              ) : (
                <CustomAutocompleteField
                  name={`${field.name}.${context.toLowerCase()}StreetAddress`}
                  control={control}
                  onChange={handleFieldChange}
                  label={streetAddressLabel}
                  placeholderText={usePlaceholder ? "Street Address" : undefined}
                  disabled={isDisabled}
                  onChangeSelect={(value) => {
                    if (value) {
                      fetchDetails((value as any).data);
                    }
                  }}
                  isMultiple={false}
                  isFreeSolo={true}
                  fetchFunction={fetchStreetSuggestions}
                  transformResponse={transformAddressResponse}
                  size={size}
                  rules={
                    isRequired
                      ? {
                          required: `${context} Street Address is required.`,
                          validate: {
                            customValidation: (value: string) => {
                              if (value?.trim() === "") {
                                return `${context} Street Address is required.`;
                              }
                            },
                          },
                        }
                      : {}
                  }
                />
              )}
            </Grid>

            <Grid item xs={xs ?? 12} md={md ?? 2} lg={lg ?? 2}>
              <CustomTextField
                controllerName={`${field.name}.${context.toLowerCase()}UnitNumber`}
                control={control}
                label={unitNumberLabel}
                placeholder={usePlaceholder ? "Unit Number" : undefined}
                isDisabled={isDisabled}
                onChange={handleFieldChange}
                size={size}
              />
            </Grid>

            <Grid item xs={xs ?? 12} md={md ?? 2} lg={lg ?? 2}>
              <CustomTextField
                controllerName={`${field.name}.${context.toLowerCase()}City`}
                control={control}
                label={cityLabel}
                placeholder={usePlaceholder ? "City" : undefined}
                isDisabled={isDisabled}
                onChange={handleFieldChange}
                size={size}
                rules={
                  isRequired
                    ? {
                        required: `${context} City is required.`,
                        validate: {
                          customValidation: (value: string) => {
                            if (value?.trim() === "") {
                              return `${context} City is required.`;
                            }
                          },
                        },
                      }
                    : {}
                }
              />
            </Grid>

            <Grid item xs={xs ?? 12} md={md ?? 2} lg={lg ?? 2}>
              <CustomDropdownField
                name={`${field.name}.${context.toLowerCase()}State`}
                control={control}
                label={stateLabel}
                placeholderText={usePlaceholder ? "State" : undefined}
                options={stateListOptions}
                disabled={isDisabled}
                onChangeSelect={handleFieldChange}
                size={size}
                rules={
                  isRequired
                    ? {
                        required: `${context} State is required.`,
                        validate: {
                          customValidation: (value: string) => {
                            if (!value && value.trim() === "") {
                              return `${context} State is required.`;
                            }
                          },
                        },
                      }
                    : {}
                }
              />
            </Grid>

            <Grid item xs={12} md={2}>
              <CustomTextField
                controllerName={`${field.name}.${context.toLowerCase()}Zipcode`}
                control={control}
                label={zipLabel}
                placeholder={usePlaceholder ? "Zip Code" : undefined}
                isDisabled={isDisabled}
                onChange={handleFieldChange}
                size={size}
                rules={
                  isRequired
                    ? {
                        required: `${context} Zip Code is required.`,
                        validate: {
                          customValidation: (value: string) => {
                            if (value?.trim() === "") {
                              return `${context} Zip Code is required.`;
                            }
                          },
                        },
                      }
                    : {}
                }
              />
            </Grid>
          </Grid>
        )}
      />
    );
  // ── no-name branch (flat field names) ────────────────────────────────────
  else
    return (
      <Grid container spacing={2}>
        <Grid item xs={xs ?? 12} md={md ?? 4} lg={lg ?? 4}>
          {useAutoComplete ? (
            <CustomAutocompleteField
              name={`${context.toLowerCase()}streetAddress`}
              control={control}
              onChange={handleFieldChange}
              label={streetAddressLabel}
              disabled={isDisabled}
              isMultiple={false}
              isFreeSolo={true}
              fetchFunction={fetchStreetSuggestions}
              transformResponse={transformAddressResponse}
              onChangeSelect={(value) => {
                if (value) {
                  fetchDetails((value as any).data);
                }
              }}
              size={size}
              rules={
                isRequired
                  ? {
                      required: `${context} Street Address is required.`,
                      validate: {
                        customValidation: (value: string) => {
                          if (value?.trim() === "") {
                            return `${context} Street Address is required.`;
                          }
                        },
                      },
                    }
                  : {}
              }
            />
          ) : (
            <CustomTextField
              controllerName={`${context.toLowerCase()}streetAddress`}
              control={control}
              label={streetAddressLabel}
              isDisabled={isDisabled}
              onChange={handleFieldChange}
              size={size}
              rules={
                isRequired
                  ? {
                      required: `${context} Street Address is required.`,
                      validate: {
                        customValidation: (value: string) => {
                          if (value?.trim() === "") {
                            return `${context} Street Address is required.`;
                          }
                        },
                      },
                    }
                  : {}
              }
            />
          )}
        </Grid>

        <Grid item xs={xs ?? 12} md={md ?? 4} lg={lg ?? 4}>
          <CustomTextField
            controllerName={`${context.toLowerCase()}unitNumber`}
            control={control}
            label={unitNumberLabel}
            isDisabled={isDisabled}
            onChange={handleFieldChange}
            size={size}
          />
        </Grid>

        <Grid item xs={xs ?? 12} md={md ?? 4} lg={lg ?? 4}>
          <CustomTextField
            controllerName={`${context.toLowerCase()}city`}
            control={control}
            label={cityLabel}
            isDisabled={isDisabled}
            onChange={handleFieldChange}
            size={size}
            rules={
              isRequired
                ? {
                    required: `${context} City is required.`,
                    validate: {
                      customValidation: (value: string) => {
                        if (value?.trim() === "") {
                          return `${context} City is required.`;
                        }
                      },
                    },
                  }
                : {}
            }
          />
        </Grid>

        <Grid item xs={xs ?? 12} md={md ?? 4} lg={lg ?? 4}>
          <CustomDropdownField
            name={`${context.toLowerCase()}state`}
            control={control}
            label={stateLabel}
            options={stateListOptions}
            disabled={isDisabled}
            onChangeSelect={handleFieldChange}
            size={size}
            rules={
              isRequired
                ? {
                    required: `${context} State is required.`,
                    validate: {
                      customValidation: (value: string) => {
                        if (!value) {
                          return `${context} State is required.`;
                        }
                      },
                    },
                  }
                : {}
            }
          />
        </Grid>

        <Grid item xs={xs ?? 12} md={md ?? 4} lg={lg ?? 4}>
          <CustomTextField
            controllerName={`${context.toLowerCase()}zipCode`}
            control={control}
            label={zipLabel}
            isDisabled={isDisabled}
            onChange={handleFieldChange}
            size={size}
            rules={
              isRequired
                ? {
                    required: `${context} Zip Code is required.`,
                    validate: {
                      customValidation: (value: string) => {
                        if (value?.trim() === "") {
                          return `${context} Zip Code is required.`;
                        }
                      },
                    },
                  }
                : {}
            }
          />
        </Grid>
      </Grid>
    );
};

export default CustomAddressTextField;
